import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Loading } from "@/components/common";
import { homePath } from "@/lib/nav";
import DashboardLayout from "@/layouts/DashboardLayout";
import { Login, Signup, ForgotPassword, ResetPassword } from "@/pages/Auth";
import Dashboard from "@/pages/admin/Dashboard";
import Orders from "@/pages/admin/Orders";
import OrderDetail from "@/pages/admin/OrderDetail";
import Customers from "@/pages/admin/Customers";
import Plans from "@/pages/admin/Plans";
import Payments from "@/pages/admin/Payments";
import Tasks from "@/pages/admin/Tasks";
import Reports from "@/pages/admin/Reports";
import Settings from "@/pages/admin/Settings";
import CustomerDashboard from "@/pages/customer/CustomerDashboard";
import CustomerOrders from "@/pages/customer/CustomerOrders";
import CustomerOrderDetail from "@/pages/customer/CustomerOrderDetail";
import PlatformDashboard from "@/pages/platform/PlatformDashboard";

function Protected({ children, roles, platform }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) return <div className="min-h-screen grid place-items-center"><Loading /></div>;
  if (user === false) return <Navigate to="/login" state={{ from: location }} replace />;
  if (platform) {
    if (user.platform_role !== "super_admin") return <Navigate to={homePath(user)} replace />;
    return children;
  }
  // tenant routes: super admin has no tenant workspace
  if (user.platform_role === "super_admin") return <Navigate to="/platform" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to={homePath(user)} replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen grid place-items-center"><Loading /></div>;
  if (user) return <Navigate to={homePath(user)} replace />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
      <Route path="/register" element={<PublicOnly><Signup /></PublicOnly>} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />

      {/* Platform super admin */}
      <Route path="/platform" element={<Protected platform><PlatformDashboard /></Protected>} />

      {/* Business Admin + Staff */}
      <Route element={<Protected roles={["admin", "staff"]}><DashboardLayout /></Protected>}>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/orders" element={<Orders />} />
        <Route path="/orders/:orderId" element={<OrderDetail />} />
        <Route path="/customers" element={<Protected roles={["admin"]}><Customers /></Protected>} />
        <Route path="/plans" element={<Protected roles={["admin"]}><Plans /></Protected>} />
        <Route path="/payments" element={<Protected roles={["admin"]}><Payments /></Protected>} />
        <Route path="/tasks" element={<Tasks />} />
        <Route path="/reports" element={<Protected roles={["admin"]}><Reports /></Protected>} />
        <Route path="/settings" element={<Protected roles={["admin"]}><Settings /></Protected>} />
      </Route>

      {/* Customer portal */}
      <Route path="/portal" element={<Protected roles={["customer"]}><CustomerDashboard /></Protected>} />
      <Route path="/portal/orders" element={<Protected roles={["customer"]}><CustomerOrders /></Protected>} />
      <Route path="/portal/orders/:orderId" element={<Protected roles={["customer"]}><CustomerOrderDetail /></Protected>} />

      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}
